import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Save, MousePointer2, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

const DISPLAY_W = 720; // px width the page image is rendered at

// Field types that carry a value and therefore need a box on the form
const VALUE_TYPES = ['text', 'textarea', 'date', 'checkbox', 'yesno', 'radio', 'select'];

export default function TemplatePositionEditor({ open, onOpenChange, template, onSaved }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [pages, setPages] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxes] = useState({});       // fieldId -> {page,x,y,w,h,font_size}
  const [fieldsMeta, setFieldsMeta] = useState([]); // {id,label,md_field,type,sampleValue}
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const drag = useRef(null);

  const page = pages[pageNo];
  const ratio = page ? DISPLAY_W / page.width : 1; // points -> px
  const dispH = page ? page.height * ratio : 0;

  useEffect(() => {
    if (!open || !template) return;
    setLoading(true);
    (async () => {
      try {
        const p = await axios.get(`${API}/application-templates/${template.id}/pages`, auth);
        setPages(p.data.pages || []);
        // seed boxes from current field positions
        const seeded = {};
        const meta = [];
        (template.fields || []).forEach((f) => {
          if (!VALUE_TYPES.includes(f.type)) return;
          meta.push({ id: f.id, label: f.label, md_field: f.md_field, type: f.type });
          if (f.position && (f.position.x !== undefined)) {
            seeded[f.id] = {
              page: f.position.page || 0,
              x: f.position.x, y: f.position.y,
              w: f.position.w || 160, h: f.position.h || 16,
              font_size: f.position.font_size || 9,
            };
          }
        });
        setBoxes(seeded);
        setFieldsMeta(meta);
        setPageNo(0);
      } catch (e) {
        toast.error('Could not load form pages');
      } finally { setLoading(false); }
    })();
  }, [open, template?.id]);

  // Load the page image (authed) as an object URL
  useEffect(() => {
    if (!open || !pages.length) return;
    let revoked = null;
    (async () => {
      try {
        const r = await axios.get(`${API}/application-templates/${template.id}/page-image/${pageNo}?zoom=2`, { ...auth, responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setImgUrl(url);
      } catch { setImgUrl(null); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [open, pageNo, pages.length, template?.id]);

  const unplaced = useMemo(() => fieldsMeta.filter((f) => !boxes[f.id] || boxes[f.id].page !== pageNo), [fieldsMeta, boxes, pageNo]);
  const placedOnPage = useMemo(() => fieldsMeta.filter((f) => boxes[f.id] && boxes[f.id].page === pageNo), [fieldsMeta, boxes, pageNo]);

  const addBox = (f) => {
    setBoxes((b) => ({ ...b, [f.id]: { page: pageNo, x: 60, y: 60, w: 180, h: 18, font_size: 9 } }));
    setSelected(f.id);
  };

  const onMouseDownBox = (e, fid, mode) => {
    e.stopPropagation();
    const b = boxes[fid];
    drag.current = { fid, mode, startX: e.clientX, startY: e.clientY, orig: { ...b } };
    setSelected(fid);
  };

  const onMouseMove = (e) => {
    if (!drag.current) return;
    const { fid, mode, startX, startY, orig } = drag.current;
    const dxPt = (e.clientX - startX) / ratio;
    const dyPt = (e.clientY - startY) / ratio;
    setBoxes((b) => {
      const cur = { ...b[fid] };
      if (mode === 'move') {
        cur.x = Math.max(0, Math.round((orig.x + dxPt) * 10) / 10);
        cur.y = Math.max(0, Math.round((orig.y + dyPt) * 10) / 10);
      } else {
        cur.w = Math.max(30, Math.round((orig.w + dxPt) * 10) / 10);
        cur.h = Math.max(12, Math.round((orig.h + dyPt) * 10) / 10);
      }
      return { ...b, [fid]: cur };
    });
  };
  const onMouseUp = () => { drag.current = null; };

  const removeBox = (fid) => setBoxes((b) => { const n = { ...b }; delete n[fid]; return n; });

  const save = async (activate) => {
    setSaving(true);
    try {
      const fields = (template.fields || []).map((f) => {
        const box = boxes[f.id];
        return {
          id: f.id, label: f.label, type: f.type, md_field: f.md_field || null,
          manual_entry: !f.md_field, required: !!f.required, default_value: f.default_value || null,
          confidence: f.confidence ?? null, options: f.options || [], acrofield_name: f.acrofield_name || null,
          position: box ? { page: box.page, x: box.x, y: box.y, w: box.w, h: box.h, font_size: box.font_size } : (f.position || null),
          date_format: f.date_format || null,
        };
      });
      await axios.put(`${API}/application-templates/${template.id}`, { fields, status: activate ? 'active' : undefined }, auth);
      toast.success(activate ? 'Positions saved & form activated' : 'Field positions saved');
      onSaved?.();
      if (activate) onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save positions'); }
    finally { setSaving(false); }
  };

  const previewNow = async () => {
    await save(false);
    try {
      const r = await axios.post(`${API}/application-templates/${template.id}/preview`, {}, { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { toast.error('Preview failed'); }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="position-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MousePointer2 className="w-5 h-5 text-teal-600" /> Position Fields on the Form</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Drag each labelled box to the empty space on the form where that value should print, and resize it to fit the box.
            Add any missing fields from the list on the right. Save, preview to check, then activate.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading form…</div>
        ) : pages.length === 0 ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            This form type can't be positioned visually (only flat / scanned PDFs support the box editor). Fillable PDFs place values into their own fields automatically.
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Canvas */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={pageNo === 0} onClick={() => setPageNo((p) => Math.max(0, p - 1))} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-xs text-slate-500">Page {pageNo + 1} / {pages.length}</span>
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={pageNo >= pages.length - 1} onClick={() => setPageNo((p) => Math.min(pages.length - 1, p + 1))} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
              <div
                ref={containerRef}
                className="relative border rounded shadow-sm select-none bg-white mx-auto"
                style={{ width: DISPLAY_W, height: dispH }}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                data-testid="position-canvas"
              >
                {imgUrl && <img src={imgUrl} alt="form" width={DISPLAY_W} height={dispH} draggable={false} style={{ display: 'block', pointerEvents: 'none' }} />}
                {placedOnPage.map((f) => {
                  const b = boxes[f.id];
                  const isSel = selected === f.id;
                  return (
                    <div
                      key={f.id}
                      className={`absolute overflow-hidden ${isSel ? 'ring-2 ring-teal-500 bg-teal-500/20' : 'bg-blue-500/15 ring-1 ring-blue-400'}`}
                      style={{ left: b.x * ratio, top: b.y * ratio, width: b.w * ratio, height: b.h * ratio, cursor: 'move' }}
                      onMouseDown={(e) => onMouseDownBox(e, f.id, 'move')}
                      onDoubleClick={() => removeBox(f.id)}
                      title={`${f.label} — drag to move, double-click to remove`}
                      data-testid={`pos-box-${f.id}`}
                    >
                      <span className="text-[9px] leading-tight text-blue-800 font-medium px-0.5 whitespace-nowrap">{f.label}</span>
                      <div
                        className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-teal-600 cursor-nwse-resize"
                        onMouseDown={(e) => onMouseDownBox(e, f.id, 'resize')}
                        data-testid={`pos-resize-${f.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Field palette */}
            <div className="w-full lg:w-64 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Fields to place</p>
              <div className="border rounded divide-y max-h-[420px] overflow-y-auto" data-testid="unplaced-fields">
                {unplaced.length === 0 && <p className="text-xs text-emerald-600 p-2">All fields placed on this page 🎉</p>}
                {unplaced.map((f) => (
                  <button key={f.id} className="w-full text-left px-2 py-1.5 text-xs hover:bg-teal-50 flex items-center justify-between gap-2" onClick={() => addBox(f)} data-testid={`add-box-${f.id}`}>
                    <span className="truncate">{f.label}</span>
                    <Badge variant="outline" className="text-[9px]">{f.md_field ? 'mapped' : 'manual'}</Badge>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Tip: double-click a box to remove it from the form.</p>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          <Button variant="outline" onClick={previewNow} disabled={saving || !pages.length} data-testid="position-preview-btn"><Eye className="w-4 h-4 mr-1.5" /> Save & Preview</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving || !pages.length} data-testid="position-save-btn"><Save className="w-4 h-4 mr-1.5" /> Save Positions</Button>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => save(true)} disabled={saving || !pages.length} data-testid="position-activate-btn">
            {saving ? 'Saving…' : 'Save & Activate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
